/**
 * Blocos do GOES-19: o mesmo JPEG do STAR, cortado sem recompressão em blocos
 * de 512 px pela máquina do agendador (AmaView-dados, `agendador/blocos`).
 *
 * De perto quase nada do quadro aparece na tela — no zoom 6, 0,8% da imagem de
 * 7200 px —, e baixar o arquivo inteiro custa 7,3 MB e 124 MB decodificados por
 * quadro. Com os blocos, a imagem inteira fica numa largura modesta (a base) e
 * só os blocos da área visível chegam na nitidez que a tela pede.
 *
 * Aqui só a geometria (pura): grade, endereços, nível e blocos à vista.
 */
import { getHeight, getWidth, type Extent } from 'ol/extent';
import { USEFUL_UPSCALE } from '../goes/budget';
import { formatStarStamp } from '../goes/stamp';

/**
 * Larguras que o servidor pode cortar em blocos (as da base vêm inteiras do
 * STAR). 3600 e 7200 existem para todo produto; 14400 (0,5 km) só para o que o
 * servidor anunciar em `/saude` (`niveis`, ver saude.ts) — a banda 02.
 */
export const NIVEIS_CONHECIDOS = [3600, 7200, 14400] as const;
export type Nivel = (typeof NIVEIS_CONHECIDOS)[number];
/** Níveis de um produto sem anúncio do servidor. */
export const NIVEIS_PADRAO: readonly Nivel[] = [3600, 7200];

export const ehNivel = (n: unknown): n is Nivel => (NIVEIS_CONHECIDOS as readonly unknown[]).includes(n);

/** Miolo de cada bloco (px do quadro). */
export const BLOCO = 512;
/**
 * Sobra de cada lado, onde há vizinho: o bloco é desenhado só no miolo, e a
 * suavização ao ampliar lê a sobra — sem emendas entre blocos.
 */
export const SOBRA = 16;

/**
 * Até esta largura a base (imagem inteira do STAR) basta; acima dela, blocos.
 * 1800 px = 0,9–1,7 MB por quadro: o loop inteiro cabe decodificado com folga.
 * De perto, com os blocos cobrindo a vista, a base desce a 900 (motor.ts,
 * `PARAMETROS.basePerto`).
 */
export const LARGURA_BASE = 1800;

/** Endereço dos blocos. `VITE_BLOCOS_URL=""` desliga (volta tudo ao arquivo inteiro). */
export const BLOCOS_URL: string = (import.meta.env.VITE_BLOCOS_URL ?? 'https://147.15.84.134/blocos').replace(/\/$/, '');

/**
 * Versão dos blocos no servidor. v2: JPEG progressivo, transformado sem
 * recompressão a partir do mesmo arquivo do STAR (os mesmos pixels, ~24% menos
 * bytes); v1: o corte simples.
 */
export const VERSAO_BLOCOS = 'v2';

/**
 * Estimativa por bloco antes de medir (544 px de nuvem em JPEG; medido de dia:
 * 130–190 KB no GEOCOLOR e na banda 02, ~95 KB na 13). Depois dos primeiros
 * blocos vale a média medida por produto e nível (tamanhos.ts).
 */
export const BYTES_POR_BLOCO = 150_000;

export const alturaDo = (nivel: Nivel) => (nivel * 3) / 5;

/** Linhas e colunas de blocos no quadro (os da borda direita e de baixo são menores). */
export function grade(nivel: Nivel): { linhas: number; colunas: number } {
  return { linhas: Math.ceil(alturaDo(nivel) / BLOCO), colunas: Math.ceil(nivel / BLOCO) };
}

export function urlDoBloco(produto: string, tempo: number, nivel: Nivel, linha: number, coluna: number): string {
  return `${BLOCOS_URL}/${VERSAO_BLOCOS}/nsa/${produto}/${formatStarStamp(tempo)}/${nivel}/${linha}_${coluna}.jpg`;
}

/** Identidade do bloco no cache (o "produto" de uma entrada do FrameCache). */
export const chaveDoBloco = (produto: string, nivel: Nivel, linha: number, coluna: number) =>
  `${produto}@${nivel}:${linha}_${coluna}`;

export interface Faixa {
  l0: number;
  l1: number;
  c0: number;
  c1: number;
}

export const blocosDaFaixa = (f: Faixa) => (f.l1 - f.l0 + 1) * (f.c1 - f.c0 + 1);

/** Blocos da faixa, na ordem (linha, coluna). */
export function* blocosDe(f: Faixa): Generator<[number, number]> {
  for (let l = f.l0; l <= f.l1; l++) for (let c = f.c0; c <= f.c1; c++) yield [l, c];
}

/**
 * Blocos que cruzam a vista (`vista` e `imagem` em unidades do mapa; `imagem` é
 * a extensão exata do quadro nesse tamanho, ver imageGeometry.ts). Null se a
 * vista não toca a imagem.
 */
export function faixaVisivel(vista: Extent, imagem: Extent, nivel: Nivel): Faixa | null {
  const w = getWidth(imagem);
  const h = getHeight(imagem);
  if (!(w > 0 && h > 0)) return null;
  const altura = alturaDo(nivel);
  const x0 = ((vista[0] - imagem[0]) / w) * nivel;
  const x1 = ((vista[2] - imagem[0]) / w) * nivel;
  const y0 = ((imagem[3] - vista[3]) / h) * altura;
  const y1 = ((imagem[3] - vista[1]) / h) * altura;
  if (!(x1 > 0 && y1 > 0 && x0 < nivel && y0 < altura)) return null;
  const { linhas, colunas } = grade(nivel);
  const limitar = (v: number, max: number) => Math.min(max - 1, Math.max(0, Math.floor(v / BLOCO)));
  return {
    l0: limitar(y0, linhas),
    l1: limitar(Math.min(y1, altura) - 1e-6, linhas),
    c0: limitar(x0, colunas),
    c1: limitar(Math.min(x1, nivel) - 1e-6, colunas),
  };
}

/**
 * Onde o miolo do bloco fica no quadro (px) e onde começa dentro do arquivo do
 * bloco (a sobra, se houver, vem antes).
 */
export function mioloDoBloco(nivel: Nivel, linha: number, coluna: number) {
  const altura = alturaDo(nivel);
  const x = coluna * BLOCO;
  const y = linha * BLOCO;
  return {
    x,
    y,
    largura: Math.min(BLOCO, nivel - x),
    altura: Math.min(BLOCO, altura - y),
    /** Deslocamento do miolo dentro do arquivo (sobra à esquerda/acima). */
    sx: coluna > 0 ? SOBRA : 0,
    sy: linha > 0 ? SOBRA : 0,
  };
}

/** Extensão (unidades do mapa) do miolo do bloco, dada a extensão do quadro. */
export function extensaoDoBloco(imagem: Extent, nivel: Nivel, linha: number, coluna: number): Extent {
  const m = mioloDoBloco(nivel, linha, coluna);
  const rx = getWidth(imagem) / nivel;
  const ry = getHeight(imagem) / alturaDo(nivel);
  return [imagem[0] + m.x * rx, imagem[3] - (m.y + m.altura) * ry, imagem[0] + (m.x + m.largura) * rx, imagem[3] - m.y * ry];
}

/**
 * Nível de detalhe que a tela pede, ou null se a base basta. Mesma conta da
 * qualidade automática (budget.ts): pixels do setor na tela × densidade,
 * aceitando ampliar até 15%. O menor nível disponível (acima da base e até o
 * `teto`) que dá conta; se nenhum dá, o maior.
 */
export function nivelPedido(
  setorCss: number | null,
  densidade: number,
  base: number = LARGURA_BASE,
  niveis: readonly Nivel[] = NIVEIS_PADRAO,
  teto: number = Infinity,
): Nivel | null {
  if (setorCss === null || !(setorCss > 0)) return null;
  const precisa = (setorCss * Math.max(1, densidade)) / USEFUL_UPSCALE;
  if (precisa <= base) return null;
  const uteis = niveis.filter((n) => n > base && n <= teto).sort((a, b) => a - b);
  if (!uteis.length) return null;
  return uteis.find((n) => n >= precisa) ?? uteis[uteis.length - 1]!;
}

/**
 * Nível de um produto para o nível pedido (que vale para todas as camadas): o
 * maior que o produto tem até ele, ou o menor do produto. Ex.: pedido 14400 →
 * 7200 no GEOCOLOR, 14400 na banda 02.
 */
export function nivelDoProduto(nivel: Nivel, niveis: readonly Nivel[] = NIVEIS_PADRAO): Nivel {
  let melhor: Nivel | null = null;
  for (const n of niveis) if (n <= nivel && (melhor === null || n > melhor)) melhor = n;
  return melhor ?? (Math.min(...niveis) as Nivel);
}

/** A faixa com `anel` blocos a mais de cada lado, dentro da grade. */
export function faixaComAnel(f: Faixa, nivel: Nivel, anel: number): Faixa {
  const { linhas, colunas } = grade(nivel);
  return {
    l0: Math.max(0, f.l0 - anel),
    l1: Math.min(linhas - 1, f.l1 + anel),
    c0: Math.max(0, f.c0 - anel),
    c1: Math.min(colunas - 1, f.c1 + anel),
  };
}

/** Blocos de `fora` que não estão em `dentro` (o anel em volta da faixa à vista). */
export function* blocosDoAnel(dentro: Faixa, fora: Faixa): Generator<[number, number]> {
  for (const [l, c] of blocosDe(fora)) {
    if (l >= dentro.l0 && l <= dentro.l1 && c >= dentro.c0 && c <= dentro.c1) continue;
    yield [l, c];
  }
}

/** A vista inteira está dentro da imagem (os blocos cobrem tudo o que aparece)? */
export function vistaDentroDaImagem(vista: Extent, imagem: Extent): boolean {
  return vista[0] >= imagem[0] && vista[1] >= imagem[1] && vista[2] <= imagem[2] && vista[3] <= imagem[3];
}

/** Pixels decodificados de um bloco (com a sobra): a conta do orçamento. */
export function pixelsDoBloco(nivel: Nivel, linha: number, coluna: number): number {
  const m = mioloDoBloco(nivel, linha, coluna);
  const { linhas, colunas } = grade(nivel);
  const w = m.largura + m.sx + (coluna < colunas - 1 ? SOBRA : 0);
  const h = m.altura + m.sy + (linha < linhas - 1 ? SOBRA : 0);
  return w * h;
}

/**
 * Reserva: o maior nível do produto abaixo de `nivel` (null se não há). Um
 * bloco que não existe nesse nível (404 — de noite o 14400 da banda 02 não é
 * gerado) é coberto pelo de baixo, do mesmo quadro.
 */
export function nivelAbaixo(nivel: Nivel, niveis: readonly Nivel[] = NIVEIS_PADRAO): Nivel | null {
  let melhor: Nivel | null = null;
  for (const n of niveis) if (n < nivel && (melhor === null || n > melhor)) melhor = n;
  return melhor;
}

/**
 * Blocos do nível `para` que cobrem a mesma área do bloco (`linha`, `coluna`)
 * do nível `de`. Os níveis dobram (3600, 7200, 14400) e o bloco é o mesmo
 * (512 px): as bordas coincidem — descendo, um bloco; subindo, 2×2 (menos na
 * borda direita e de baixo).
 */
export function faixaCorrespondente(de: Nivel, para: Nivel, linha: number, coluna: number): Faixa {
  const { linhas, colunas } = grade(para);
  const r = para / de;
  return {
    l0: Math.min(linhas - 1, Math.floor(linha * r)),
    l1: Math.min(linhas - 1, Math.ceil((linha + 1) * r) - 1),
    c0: Math.min(colunas - 1, Math.floor(coluna * r)),
    c1: Math.min(colunas - 1, Math.ceil((coluna + 1) * r) - 1),
  };
}
